<template>
    <div ref="root">
        <div v-for="name in rejectedNames" :key="name" class="alert alert-danger">
            Typ souboru <strong>{{ name }}</strong> není podporován.
        </div>

        <file-picker v-if="photosData.length === 0 && uploads.length === 0"
                     class="mb-3"
                     :compact="false"
                     button-label="Přidat fotografie"
                     button-class="btn-primary"
                     @selected="filesSelected"
        >
            <span class="fw-semibold d-block">Přetáhněte sem fotografie nemovitosti</span>
            <span class="text-muted small">nebo klikněte a vyberte je, první bude titulní</span>
        </file-picker>

        <div v-else class="card shadow-sm mb-3">
            <div class="card-body d-flex flex-wrap align-items-center gap-3">
                <file-picker :compact="true" button-label="Přidat fotografie" button-class="btn-primary" @selected="filesSelected"/>
                <div>
                    <div class="fw-semibold">{{ summary }}</div>
                    <div class="small text-muted">Nebo je přetáhněte kamkoli na stránku. Nahrávají se v pořadí, v jakém je vyberete.</div>
                </div>
                <button v-if="canRemove && ! selecting && photosData.length > 1"
                        type="button"
                        class="btn btn-outline-secondary btn-sm ms-auto"
                        @click="startSelecting"
                >
                    <span class="far fa-check-square me-1"></span> Vybrat více
                </button>
            </div>
            <div v-if="photosData.length > 0" class="card-footer bg-white small text-muted">
                <span class="fas fa-star text-warning me-1"></span>
                První fotografie je titulní, v dokumentu je na hlavním místě.
                <template v-if="canChangePhotoPosition">Pořadí změníte přetažením.</template>
            </div>
        </div>

        <div v-if="selecting" class="alert alert-primary d-flex align-items-center gap-3 py-2">
            <span class="fw-semibold">Vybráno {{ selectedIds.length }}</span>
            <button type="button" class="btn btn-sm btn-danger" :disabled="selectedIds.length === 0" @click="removeSelected">
                <span class="fas fa-trash me-1"></span> Smazat vybrané
            </button>
            <button type="button" class="btn btn-sm btn-link ms-auto" @click="stopSelecting">Zrušit výběr</button>
        </div>

        <p class="text-muted mb-3" v-if="isBusy">
            <span class="spinner-border spinner-border-sm me-2 align-middle" role="status"></span>
            Nahráno {{ doneCount }} z {{ totalCount }}.
            <template v-if="canChangePhotoPosition">Pořadí fotografií můžete měnit, až nahrávání skončí.</template>
        </p>

        <draggable v-if="photosData.length > 0 || uploads.length > 0"
                   v-model="photosData"
                   class="row g-3 mb-4"
                   item-key="id"
                   handle=".photo-tile-thumb"
                   :disabled="! canChangePhotoPosition || isBusy || selecting"
                   @end="onDragEnd"
        >
            <template #item="{element: photo, index}">
                <div class="col-xl-3 col-lg-4 col-sm-6">
                    <photo-tile :photo="photo"
                                :url="photoUrl(photo)"
                                :is-title="index === 0"
                                :is-last="index === photosData.length - 1"
                                :can-describe="canDescribe"
                                :can-rotate="canRotate"
                                :can-move="canChangePhotoPosition && ! isBusy"
                                :can-remove="canRemove"
                                :rotating="rotatingIds.includes(photo.id)"
                                :description-state="descriptionStates[photo.id] ?? null"
                                :selecting="selecting"
                                :selected="selectedIds.includes(photo.id)"
                                @open="openLightbox(index)"
                                @toggle-selected="toggleSelected(photo)"
                                @describe="(text: string) => describe(photo, text)"
                                @rotate="(angle: number) => rotate(photo, angle)"
                                @make-title="changePosition(photo, 0)"
                                @move="(step: number) => changePosition(photo, index + step)"
                                @remove="removeLater([photo], 'Fotografie smazána.')"
                    />
                </div>
            </template>
            <template #footer>
                <div class="col-xl-3 col-lg-4 col-sm-6" v-for="upload in uploads" :key="upload.key">
                    <upload-tile :upload="upload" @retry="retry(upload.key)" @discard="discard(upload.key)"/>
                </div>
            </template>
        </draggable>

        <photo-lightbox ref="lightbox"
                        :items="lightboxItems"
                        :title-badge="true"
                        :can-describe="canDescribe"
                        :can-rotate="canRotate"
                        :rotating-ids="rotatingIds"
                        @describe="(id: string, text: string) => describe(findPhoto(id), text)"
                        @rotate="(id: string, angle: number) => rotate(findPhoto(id), angle)"
        />

        <undo-toast :message="removalMessage" @undo="undoRemoval"/>

        <teleport to="body">
            <div v-if="isDragging" class="page-drop-overlay">
                <span class="fas fa-images fa-3x mb-3"></span>
                Pusťte fotografie, přidají se na konec
            </div>
        </teleport>
    </div>
</template>

<script lang="ts">
import {useOrderedUploads} from '@/composables/OrderedUploads';
import {usePageFileDrop} from '@/composables/PageFileDrop';
import {usePendingRemoval} from '@/composables/PendingRemoval';
import {Segment} from '@/composables/Segment';
import {EstimateState, Photo} from '@/model/api';
import {Estimate} from '@/model/Estimate';
import {right} from 'fp-ts/lib/Either';
import {computed, defineComponent, inject, PropType, ref} from 'vue';
import draggable from 'zhyswan-vuedraggable';
import FilePicker from './controls/FilePicker.vue';
import UndoToast from './controls/UndoToast.vue';
import UploadTile from './controls/UploadTile.vue';
import PhotoLightbox from './photos/PhotoLightbox.vue';
import PhotoTile, {DescriptionState} from './photos/PhotoTile.vue';
import {plural, splitSupportedFiles} from './photos/texts';

export default defineComponent({
    components: {
        FilePicker,
        PhotoLightbox,
        PhotoTile,
        UndoToast,
        UploadTile,
        draggable,
    },
    props: {
        photos: {
            type: Array as PropType<Photo[]>,
            required: true,
        },
        estimate: {
            type: Object as PropType<EstimateState>,
            required: true,
        },
    },
    setup(props, {expose}) {
        const model = inject<Estimate<EstimateState>>('model')!;

        const canChangePhotoPosition = model.changePhotoPosition !== undefined;
        const canRotate = model.rotatePhoto !== undefined;
        const canDescribe = model.changePhotoDescription !== undefined;
        const canRemove = model.removePhoto !== undefined;

        const root = ref<HTMLElement | null>(null);
        const lightbox = ref<InstanceType<typeof PhotoLightbox> | null>(null);
        const estimateState = ref<EstimateState>(props.estimate);
        const photosData = ref<Photo[]>([...props.photos]);
        const rotatingIds = ref<string[]>([]);
        // a rotated photo keeps its address, a new version makes the browser load it again
        const versions = ref<{[photoId: string]: number}>({});
        const descriptionStates = ref<{[photoId: string]: DescriptionState}>({});
        const rejectedNames = ref<string[]>([]);
        const selecting = ref(false);
        const selectedIds = ref<string[]>([]);

        const {uploads, isBusy, doneCount, totalCount, add, nextPosition, retry, discard, clearShown} = useOrderedUploads(
            (file: File, position: number) => model.uploadPhoto!(file, position),
            refresh,
        );

        const removal = usePendingRemoval(
            photosData,
            (photo: Photo) => model.removePhoto!(photo.id),
            refresh,
        );

        const {isDragging} = usePageFileDrop(filesSelected, () => root.value?.offsetParent !== null);

        const summary = computed(() => {
            const count = photosData.value.length;
            const withoutDescription = photosData.value.filter(photo => photo.description.trim() === '').length;
            const text = `${count} ${plural(count, 'fotografie', 'fotografie', 'fotografií')}`;

            if (! canDescribe || count === 0) {
                return text;
            }

            return withoutDescription > 0 ? `${text}, ${withoutDescription} bez popisku` : `${text}, všechny s popiskem`;
        });

        const lightboxItems = computed(() => photosData.value.map(photo => ({
            id: photo.id,
            url: photoUrl(photo),
            description: photo.description,
        })));

        function refresh(): void {
            model.getEstimate()
                .then(estimate => {
                    estimateState.value = estimate;

                    photosData.value = estimate.photos!.filter(photo => ! removal.isPending(photo.id));

                    clearShown(photo => photosData.value.some(shownPhoto => shownPhoto.id === photo.id));
                });
        }

        function filesSelected(files: File[]): void {
            const {supported, rejectedNames: rejected} = splitSupportedFiles(files);

            rejectedNames.value = rejected;

            if (supported.length > 0) {
                const savedPhotos = [...photosData.value, ...removal.pendingItems()];

                add(supported, nextPosition(savedPhotos.map(photo => photo.position)));
            }
        }

        function photoUrl(photo: Photo): string {
            const version = versions.value[photo.id];

            if (version === undefined) {
                return photo.url;
            }

            const url = new URL(photo.url, window.location.href);
            url.searchParams.set('t', String(version));

            return url.toString();
        }

        function findPhoto(id: string): Photo {
            return photosData.value.find(photo => photo.id === id)!;
        }

        function rotate(photo: Photo, angle: number): void {
            rotatingIds.value = [...rotatingIds.value, photo.id];

            model.rotatePhoto!(photo.id, angle)
                .then(() => {
                    versions.value = {...versions.value, [photo.id]: Date.now()};
                })
                .finally(() => {
                    rotatingIds.value = rotatingIds.value.filter(id => id !== photo.id);
                });
        }

        function describe(photo: Photo, description: string): void {
            if (description === photo.description) {
                return;
            }

            descriptionStates.value = {...descriptionStates.value, [photo.id]: 'saving'};

            model.changePhotoDescription!(photo.id, description)
                .then(() => {
                    photo.description = description;
                    descriptionStates.value = {...descriptionStates.value, [photo.id]: 'saved'};

                    window.setTimeout(() => {
                        if (descriptionStates.value[photo.id] === 'saved') {
                            descriptionStates.value = {...descriptionStates.value, [photo.id]: null};
                        }
                    }, 2000);
                })
                .catch(() => {
                    descriptionStates.value = {...descriptionStates.value, [photo.id]: 'failed'};
                });
        }

        async function changePosition(photo: Photo, position: number): Promise<void> {
            // the visible order matches the server only without the photos waiting to be removed
            await removal.commit();
            await model.changePhotoPosition!(photo.id, position);

            refresh();
        }

        function onDragEnd(event: {oldIndex: number; newIndex: number}): void {
            if (event.oldIndex === event.newIndex) {
                return;
            }

            changePosition(photosData.value[event.newIndex], event.newIndex);
        }

        function openLightbox(index: number): void {
            lightbox.value?.open(index);
        }

        function startSelecting(): void {
            selecting.value = true;
            selectedIds.value = [];
        }

        function stopSelecting(): void {
            selecting.value = false;
            selectedIds.value = [];
        }

        function toggleSelected(photo: Photo): void {
            selectedIds.value = selectedIds.value.includes(photo.id)
                ? selectedIds.value.filter(id => id !== photo.id)
                : [...selectedIds.value, photo.id];
        }

        function removeSelected(): void {
            const photos = photosData.value.filter(photo => selectedIds.value.includes(photo.id));
            const count = photos.length;

            stopSelecting();
            removal.removeLater(
                photos,
                `${plural(count, 'Smazána', 'Smazány', 'Smazáno')} ${count} ${plural(count, 'fotografie', 'fotografie', 'fotografií')}.`,
            );
        }

        async function submit() {
            return right(estimateState.value);
        }

        function hasNotSubmittedChanges(): boolean {
            return false; // this segment does not use submit button
        }

        function clearNotSubmittedChanges(): void {
        }

        function allowsMovingOn(): boolean {
            return true;
        }

        function beforeEnter(): void {
        }

        const segmentApi: Segment<EstimateState> = {
            hasNotSubmittedChanges,
            clearNotSubmittedChanges,
            submit,
            allowsMovingOn,
            beforeEnter,
        };

        expose(segmentApi);

        return {
            canChangePhotoPosition,
            canRotate,
            canDescribe,
            canRemove,
            root,
            lightbox,
            photosData,
            rotatingIds,
            descriptionStates,
            rejectedNames,
            selecting,
            selectedIds,
            uploads,
            isBusy,
            doneCount,
            totalCount,
            isDragging,
            summary,
            lightboxItems,
            removalMessage: removal.message,
            removeLater: removal.removeLater,
            undoRemoval: removal.undo,
            filesSelected,
            retry,
            discard,
            photoUrl,
            findPhoto,
            rotate,
            describe,
            changePosition,
            onDragEnd,
            openLightbox,
            startSelecting,
            stopSelecting,
            toggleSelected,
            removeSelected,
            ...segmentApi,
        };
    },
});
</script>
